import { X, Network, Grid3x3, Circle, Workflow, GitBranch, Layers } from 'lucide-react';

const LayoutAlgorithmModal = ({ isOpen, onClose, onConfirm, nodeCount }) => {
  if (!isOpen) return null;

  const algorithms = [
    {
      id: 'hierarchical',
      name: 'Hierarchical (BFS)',
      description: 'Organizes entities in hierarchical levels based on relationships',
      icon: Layers,
      recommended: true,
      bestFor: 'Database schemas with clear parent-child relationships'
    },
    {
      id: 'sugiyama',
      name: 'Sugiyama (Layered)',
      description: 'Minimizes edge crossings with layered horizontal layout',
      icon: GitBranch,
      bestFor: 'Complex schemas with many relationships'
    },
    {
      id: 'force',
      name: 'Force-Directed',
      description: 'Simulates physical forces between entities for natural spacing',
      icon: Network,
      bestFor: 'Medium-sized schemas (< 50 entities)'
    },
    {
      id: 'grid',
      name: 'Grid Layout',
      description: 'Arranges entities in a uniform grid pattern',
      icon: Grid3x3,
      bestFor: 'Simple organization without considering relationships'
    },
    {
      id: 'circular',
      name: 'Circular Layout',
      description: 'Places entities evenly around a circle',
      icon: Circle,
      bestFor: 'Small schemas or exploring entity connections'
    },
    {
      id: 'radial',
      name: 'Radial Tree',
      description: 'Root entity at center with children radiating outward',
      icon: Workflow,
      bestFor: 'Highlighting a main entity with its relationships'
    }
  ];

  const handleSelect = (algorithmId) => {
    onConfirm(algorithmId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Choose Layout Algorithm
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select how to organize your {nodeCount} entities
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {algorithms.map((algo) => {
              const Icon = algo.icon;
              return (
                <button
                  key={algo.id}
                  onClick={() => handleSelect(algo.id)}
                  className="w-full text-left p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                      <Icon size={24} className="text-blue-600 dark:text-blue-400" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {algo.name}
                        </h3>
                        {algo.recommended && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {algo.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                        <span className="font-medium">Best for:</span>
                        <span>{algo.bestFor}</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Warning */}
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Warning: Current positions will be lost
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  All entities will be repositioned according to the selected algorithm. This action cannot be undone (unless you don't save the model).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayoutAlgorithmModal;
